import { Component, ElementRef, inject, OnInit, viewChild } from '@angular/core';
import { HeaderComponent } from '../../core/shared/layout/header/header.component';
import { BottomNavigationComponent } from '../../core/shared/layout/bottom-navigation/bottom-navigation.component';
import { FooterComponent } from '../../core/shared/layout/footer/footer.component';
import { AsyncPipe, NgStyle, NgTemplateOutlet, SlicePipe } from '@angular/common';
import { UserVipService } from '../../core/services/user/user-vip.service';
import { UserService } from '../../core/services/user/user.service';
import { VipLevelLabelComponent } from './vip-level-label/vip-level-label.component';
import { VipProgressBarComponent } from './vip-progress-bar/vip-progress-bar.component';
import { RouterLink } from '@angular/router';
import { BonusStage } from '../../core/services/user/data/user-bonuses.data';
import { SliderComponent } from '../../core/modules/slider/slider.component';
import { CopyTextService } from '../../core/services/copy-text.service';
import { GameListConfig } from '../../core/shared/interfaces/game-list-config-interface';
import { isMobile } from '../../core/helpers/device';
import { TranslatePipe } from '../../core/shared/translation/translate.pipe';
import { SideNavComponent } from '../../core/shared/layout/side-nav/side-nav.component';
import { BottomTermsComponent } from '../../core/shared/standalone/bottom-terms/bottom-terms.component';
import { SliderGameListComponent } from '../../core/shared/layout/slider-game-list/slider-game-list.component';
import {
  TournamentPreviewComponent,
} from '../../core/shared/standalone/tournament-preview/tournament-preview.component';
import { BonusPreviewComponent } from '../../core/shared/standalone/bonus-preview/bonus-preview.component';
import { VipEarnComponent } from './vip-earn/vip-earn.component';
import { WrapHtmlDirective } from '../../core/shared/directives/wrap-html.directive';
import { catchError, filter, first, switchMap, tap } from 'rxjs/operators';
import { merge, of } from 'rxjs';
import { ToastMessageService } from '../../core/modules/toast-message/toast-message.service';
import { LootboxService } from '../../core/services/lootbox/lootbox.service';
import { untilDestroyed } from 'ngx-unificator/rxjs';
import { WebSocketService } from '../../core/services/web-socket.service';
import { PreloaderComponent } from '../../core/shared/components/preloader/preloader.component';
import { UntilDestroy } from 'ngx-unificator/decorator';
import { EventType } from '../promo/base-promo';

@UntilDestroy()
@Component({
  selector: 'app-vip-page',
  imports: [
    FooterComponent,
    HeaderComponent,
    BottomNavigationComponent,
    SideNavComponent,
    BottomTermsComponent,
    SliderComponent,
    SliderGameListComponent,
    TournamentPreviewComponent,
    BonusPreviewComponent,
    AsyncPipe,
    NgStyle,
    NgTemplateOutlet,
    VipLevelLabelComponent,
    VipProgressBarComponent,
    VipEarnComponent,
    RouterLink,
    SlicePipe,
    TranslatePipe,
    WrapHtmlDirective,
    PreloaderComponent
  ],
  templateUrl: './vip-page.component.html',
  styleUrl: './vip-page.component.scss',
})
export class VipPageComponent implements OnInit {
  readonly levelsSlider = viewChild<SliderComponent>('levelsSlider');
  readonly rules = viewChild<ElementRef>('rules');

  public vip$: UserVipService = inject(UserVipService);
  public user$: UserService = inject(UserService);
  public copy$: CopyTextService = inject(CopyTextService);
  private _lootbox: LootboxService = inject(LootboxService);
  private _toastMessage: ToastMessageService = inject(ToastMessageService);
  private _ws: WebSocketService = inject(WebSocketService);

  public lastPlayedListConfig: GameListConfig = {
    filter: {
      limit: 5,
    },
    title: this.vip$.store.page?.VipGamesTitle,
    link: '/games/last-played',
    type: 'last-played',
  };

  ngOnInit() {
    if (this.user$.auth) {
      this._updateBonuses();
      this._wsBonusUpdate();
    }
  }

  showMore(count: number) {
    this.vip$.vipStore.storeSignal.update(store => {
      return {
        ...store,
        visibleRulesCount: count,
      };
    });
    const rules = this.rules();
    rules.nativeElement.style.height = 'unset';
    rules.nativeElement.style.overflow = 'unset';
  }

  /**
   * Activate provided bonus
   *
   * @param bonus
   */
  activate(bonus: any) {
    bonus.loading = true;
    let path;
    if (this._isFSBonus(bonus)) {
      path = this.vip$.bonuses$.activateFreeSpins(bonus.id);
    } else {
      path = this.vip$.bonuses$.activateBonus(bonus.id);
    }

    path
      .pipe(
        tap(() => {
          bonus.pending_activation = true;
          bonus.activatable = false;
          this._toastMessage.success('t.few-moments');
        }),
        catchError(error => {
          this._toastMessage.error('t.error');
          return of(error);
        }),
      )
      .subscribe(() => {
        bonus.loading = false;
      });
  }
  /**
   * Cancel provided bonus
   *
   * @param bonus
   */
  cancel(bonus: any) {
    const modal = this.vip$.modal$.open('BONUS_CANCEL_CONFIRM');
    let path;
    if (this._isFSBonus(bonus)) {
      path = this.vip$.bonuses$.cancelFreeSpins(bonus.id);
    } else {
      path = this.vip$.bonuses$.cancelBonus(bonus.id);
    }

    modal
      .onResult()
      .pipe(
        first(),
        filter(confirm => !!confirm),
        switchMap(() => {
          bonus.loading = true;
          return path;
        }),
        tap(() => {
          this._toastMessage.success('t.bonus-canceled');
          this._updateBonuses();
        }),
        catchError(error => {
          this._toastMessage.error('t.error');
          return of(error);
        }),
      )
      .subscribe(() => {
        bonus.loading = false;
      });
  }
  /**
   * Cancel lootbox
   * @param lootboxId
   */
  onCancelLootbox(lootboxId: number) {
    this._lootbox
      .cancelLootbox(lootboxId)
      .pipe(
        tap(() => {
          this._updateBonuses();
        }),
      )
      .subscribe();
  }

  /**
   * Open lootbox modal
   * @param lootboxIssued
   */
  async onOpenLootbox(lootboxIssued: boolean) {
    if (lootboxIssued) {
      const component = await import('../../core/modal-v2/components/lazy/lootbox/lootbox.component');
      const modal = await this.vip$.modal$.openLazy(component?.LootboxComponent, {
        template: 'CLEAR',
        data: { lootbox: lootboxIssued },
      });
      modal
        .onResult()
        .pipe(
          tap(() => {
            if (this._lootbox.currentLootboxPrize) {
              this._lootbox.currentLootboxPrize = null;
              this._updateBonuses();
            }
          }),
        )
        .subscribe();
    }
  }

  /**
   * Check is bonus FS
   *
   * @private
   */
  private _isFSBonus(bonus) {
    return bonus.activation_path && bonus.activation_path.includes('freespins');
  }

  private _updateBonuses() {
    this.vip$.bonuses$.bonuses$ = null;
    this.vip$.bonuses$.updateBonuses().pipe(first()).subscribe();
  }

  private _wsBonusUpdate() {
    merge(
      this._ws.bonuses$,
      this._ws.lootboxes$,
      this._ws.freespins$
    ).pipe(
      untilDestroyed(this),
      filter(res => res.stage === 'activated'),
      tap(() => this._updateBonuses())
    ).subscribe();
  }

  protected readonly isMobile = isMobile;
  protected readonly BonusStage = BonusStage;
  protected readonly EventType = EventType;
}
